import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';
import { notificationKeys } from '../notifications/hooks';
import type { InboxItem } from '../notifications/types';
import { isMessagesNotification, notificationHref } from '../notifications/types';
import { useRooms } from './hooks';
import { HUDDLE_CHAT_ID } from './huddleChat';
import { inertText } from './inertHtml';
import { roomTitle } from './rooms';
import type { RoomListItem } from './rooms';
import {
  POP_EVENTS,
  alertPermission,
  claimAlertTab,
  firstLine,
  isAlertTab,
  isMutedRoom,
  isQuietRoom,
  pageHasFocus,
  playChime,
  releaseAlertTab,
  roomsThatWentUp,
  showAlert,
  soundOn,
  titleWithCount,
  unlockSound,
} from './desktopAlerts';
import type { AlertContent } from './desktopAlerts';

/**
 * The newest top-level room message by someone else in each of these rooms,
 * for the pop-up's sender and first line. One small read per poll that saw a
 * room go up, never per poll otherwise.
 *
 * Not fetchRoomLatestMessages: that reads every message in every room with no
 * limit and no author id (rooms.ts:148-185), so it cannot skip my own.
 */
export interface AlertMessage {
  id: string;
  projectId: string;
  authorName: string;
  text: string;
}

export async function fetchNewestFromOthers(
  client: SupabaseClient,
  roomIds: string[],
  myId: string,
): Promise<Map<string, AlertMessage>> {
  const out = new Map<string, AlertMessage>();
  if (roomIds.length === 0 || myId === '') {
    return out;
  }
  // Dev4: one read per room, newest one row each. A single read across all
  // the rooms with one shared limit let a busy room fill the limit and push a
  // quieter room (a DM) out, so that DM never popped. Most likely after a
  // laptop wakes: many rooms rise in one poll.
  const replies = await Promise.all(
    roomIds.map((roomId) =>
      client
        .from('task_comments')
        .select('id, project_id, body, created_at, profiles:author_id (display_name, email)')
        .eq('project_id', roomId)
        .is('task_id', null)
        .is('parent_comment_id', null)
        .is('deleted_at', null)
        .neq('author_id', myId)
        .order('created_at', { ascending: false })
        .limit(1),
    ),
  );
  const data: unknown[] = [];
  for (const { data: rows, error } of replies) {
    if (error) {
      throw new Error(error.message);
    }
    data.push(...(Array.isArray(rows) ? rows : []));
  }
  for (const row of data as {
    id: string;
    project_id: string;
    body: string;
    profiles: { display_name: string; email: string } | null;
  }[]) {
    if (out.has(row.project_id)) {
      continue;
    }
    out.set(row.project_id, {
      id: row.id,
      projectId: row.project_id,
      authorName: personName(row.profiles),
      text: firstLine(inertText(row.body)),
    });
  }
  return out;
}

/** One message by id, for a mention or Alex Huddle pop (the Inbox row has no text). */
export async function fetchAlertBodies(
  client: SupabaseClient,
  commentIds: string[],
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  if (commentIds.length === 0) {
    return out;
  }
  const { data, error } = await client
    .from('task_comments')
    .select('id, body')
    .in('id', commentIds);
  if (error) {
    throw new Error(error.message);
  }
  for (const row of (Array.isArray(data) ? data : []) as { id: string; body: string }[]) {
    out.set(row.id, firstLine(inertText(row.body)));
  }
  return out;
}

function personName(profile: { display_name: string; email: string } | null): string {
  if (profile === null) {
    return 'Someone';
  }
  return profile.display_name.trim() !== '' ? profile.display_name : profile.email;
}

/**
 * A pop-up-worthy Inbox row: new to this tab, unread, from Messages (the
 * database's own marker, npc r3), and pointing at a message. A thread reply
 * in the quiet room (Agents) does not pop; an @ there does (D3).
 */
export function newAlertRows(
  seen: Set<string>,
  items: InboxItem[],
  quietRoomIds: ReadonlySet<string> = new Set(),
): InboxItem[] {
  return items.filter(
    (item) =>
      !seen.has(item.id) &&
      !item.is_read &&
      POP_EVENTS.has(item.event_type) &&
      isMessagesNotification(item) &&
      typeof item.comment_id === 'string' &&
      item.comment_id !== '' &&
      !(
        item.event_type === 'thread_reply' &&
        item.project_id !== null &&
        quietRoomIds.has(item.project_id)
      ),
  );
}

export function roomAlertContent(
  message: AlertMessage,
  room: RoomListItem | undefined,
  roomName: string,
): AlertContent {
  const inDm = room?.kind === 'dm';
  return {
    tag: message.id,
    title: inDm ? message.authorName : `${message.authorName} in ${roomName}`,
    body: message.text === '' ? 'Sent an attachment' : message.text,
    href: `/messages?room=${encodeURIComponent(message.projectId)}&comment=${encodeURIComponent(message.id)}`,
  };
}

export function inboxAlertContent(item: InboxItem, text: string): AlertContent {
  const who = item.actor_name === '' ? 'Someone' : item.actor_name;
  const huddle = item.event_type === 'huddle_comment';
  const where = huddle ? 'TAP Console' : (item.project_name ?? '');
  return {
    tag: item.comment_id ?? item.id,
    title: huddle
      ? `${who} in ${where}`
      : where === ''
        ? `${who} mentioned you`
        : `${who} mentioned you in ${where}`,
    body: text === '' ? 'Open it in Messages' : text,
    href: huddle ? `/messages?room=${HUDDLE_CHAT_ID}` : notificationHref(item),
  };
}

/**
 * The app-wide watcher. Mounted once, in TapLayout, next to the sidebar
 * badge it shares its numbers with (Shells.tsx). It:
 * 1. writes "(N) <title>" into the tab title from the same badge text;
 * 2. pops and chimes when a room's unread goes up, or a new Messages @ or
 *    Alex Huddle row reaches the Inbox, while this tab is not in front.
 */
export function useMessageAlerts(input: {
  badge: string | null;
  roomUnread: Map<string, number> | undefined;
  inbox: InboxItem[] | undefined;
}): void {
  const { client, access } = useAuth();
  const myId = access.status === 'approved' ? access.profile.id : '';
  const navigate = useNavigate();
  const rooms = useRooms();
  const queryClient = useQueryClient();

  // Everything the async pop path reads, kept fresh without re-running effects.
  const live = useRef({ client, myId, navigate, rooms: rooms.data });
  live.current = { client, myId, navigate, rooms: rooms.data };

  /** Comment ids already popped (or already there on load) in this tab. */
  const popped = useRef(new Set<string>());

  // ── 1. Tab title (D4) ──
  const baseTitle = useRef<string | null>(null);
  useEffect(() => {
    if (baseTitle.current === null) {
      baseTitle.current = document.title;
    }
    document.title = titleWithCount(baseTitle.current, input.badge);
  }, [input.badge]);
  useEffect(
    () => () => {
      if (baseTitle.current !== null) {
        document.title = baseTitle.current;
      }
    },
    [],
  );

  // ── Which tab alerts, and the sound unlock ──
  useEffect(() => {
    const claim = () => {
      if (document.visibilityState === 'visible') {
        claimAlertTab();
      }
    };
    const release = () => releaseAlertTab();
    // Dev4: only open an AudioContext for someone who will actually hear it.
    const unlock = () => {
      if (soundOn() && alertPermission() === 'granted') {
        unlockSound();
      }
    };
    claim();
    window.addEventListener('focus', claim);
    document.addEventListener('visibilitychange', claim);
    window.addEventListener('pagehide', release);
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('focus', claim);
      document.removeEventListener('visibilitychange', claim);
      window.removeEventListener('pagehide', release);
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
      release();
    };
  }, []);

  /** Shows each new pop-up, then one chime for the batch, and only if one showed. */
  function deliver(contents: AlertContent[]): void {
    let shownAny = false;
    for (const content of contents) {
      if (popped.current.has(content.tag)) {
        continue;
      }
      popped.current.add(content.tag);
      const shown = showAlert(content, (href) => {
        void live.current.navigate(href);
      });
      shownAny = shownAny || shown;
    }
    if (shownAny && soundOn()) {
      playChime();
    }
  }

  // Dev4: no pop-up permission, no alert at all. Without this the sound played
  // on its own for anyone who never turned alerts on (or who blocked them),
  // and each increase still cost a read for a pop-up that could not show.
  function mayAlert(): boolean {
    return (
      live.current.myId !== '' && alertPermission() === 'granted' && !pageHasFocus() && isAlertTab()
    );
  }

  // ── 2a. A room went up (D2, D3) ──
  const previousUnread = useRef<Map<string, number> | undefined>(undefined);
  useEffect(() => {
    const previous = previousUnread.current;
    previousUnread.current = input.roomUnread;
    const up = roomsThatWentUp(previous, input.roomUnread);
    if (up.length === 0) {
      return;
    }
    // An @ in a room raises that room's unread, and the Inbox poll no longer
    // runs in a hidden tab, so refresh the Inbox here (before the pop-up
    // checks, so a hidden tab does it too). Effect 2b pops from the new rows.
    void queryClient.invalidateQueries({ queryKey: notificationKeys.inbox() });
    if (!mayAlert()) {
      return;
    }
    // Dev4: until the room list is loaded the Agents room cannot be told
    // apart (isQuietRoom(undefined) is false), so a failed or slow room list
    // would let Agents pop. No list, no room pop; the title still counts.
    const roomsNow = live.current.rooms;
    if (roomsNow === undefined) {
      return;
    }
    const loud = up.filter((roomId) => {
      const room = roomsNow.find((candidate) => candidate.id === roomId);
      return !isQuietRoom(room) && !isMutedRoom(roomId);
    });
    if (loud.length === 0) {
      return;
    }
    const { client: c, myId: me } = live.current;
    fetchNewestFromOthers(c, loud, me)
      .then((newest) => {
        if (!mayAlert()) {
          return;
        }
        const contents: AlertContent[] = [];
        for (const message of newest.values()) {
          const room = (live.current.rooms ?? []).find((r) => r.id === message.projectId);
          const name = room === undefined ? 'Messages' : roomTitle(room, undefined, me);
          contents.push(roomAlertContent(message, room, name));
        }
        deliver(contents);
      })
      .catch(() => undefined);
    // deliver and mayAlert read refs only, so input.roomUnread is the one dependency.
  }, [input.roomUnread]);

  // ── 2b. A new @ or Alex Huddle row in the Inbox (D2, D3) ──
  const inboxSeen = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (input.inbox === undefined) {
      return;
    }
    if (inboxSeen.current === null) {
      // First load: what is already there never pops.
      inboxSeen.current = new Set(input.inbox.map((item) => item.id));
      for (const item of input.inbox) {
        if (typeof item.comment_id === 'string') popped.current.add(item.comment_id);
      }
      return;
    }
    const quiet = new Set(
      (live.current.rooms ?? []).filter((room) => isQuietRoom(room)).map((room) => room.id),
    );
    const fresh = newAlertRows(inboxSeen.current, input.inbox, quiet).filter(
      (item) => item.project_id === null || !isMutedRoom(item.project_id),
    );
    for (const item of input.inbox) {
      inboxSeen.current.add(item.id);
    }
    if (fresh.length === 0 || !mayAlert()) {
      return;
    }
    const ids = fresh.map((item) => item.comment_id as string);
    fetchAlertBodies(live.current.client, ids)
      .catch(() => new Map<string, string>())
      .then((bodies) => {
        if (!mayAlert()) {
          return;
        }
        deliver(
          fresh.map((item) => inboxAlertContent(item, bodies.get(item.comment_id as string) ?? '')),
        );
      })
      .catch(() => undefined);
  }, [input.inbox]);
}
